import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const DEFAULT_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Single-select “rows per page” control: trigger + menu with checkmark on the active option.
 */
export function ProductCatalogPageSizeSelect({
    value,
    onChange,
    options = DEFAULT_OPTIONS,
    disabled = false,
    testId,
    className = "",
    /** When true, the menu opens above the trigger (e.g. near the bottom of the viewport). */
    dropUp = false,
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const n = Number(value);
    const current = options.includes(n) ? n : options[0];

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className={`relative inline-flex ${className}`}>
            <button
                type="button"
                disabled={disabled}
                data-testid={testId ? `${testId}_trigger` : undefined}
                aria-expanded={open}
                aria-haspopup="listbox"
                className="flex h-8 min-w-[4.25rem] items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 text-left text-sm font-normal text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-100"
                onClick={() => !disabled && setOpen((o) => !o)}
            >
                <span className="tabular-nums">{current}</span>
                <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
                    aria-hidden
                />
            </button>

            {open && (
                <div
                    className={`absolute left-0 z-[100] min-w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg ${
                        dropUp ? "bottom-full mb-1" : "top-full mt-1"
                    }`}
                    role="listbox"
                    data-testid={testId ? `${testId}_panel` : undefined}
                >
                    {options.map((opt) => {
                        const selected = opt === current;
                        return (
                            <button
                                key={opt}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                className="flex w-full min-w-[5rem] items-center justify-between gap-2 px-3 py-2.5 text-left text-sm tabular-nums text-gray-900 hover:bg-gray-50"
                                onClick={() => {
                                    onChange(opt);
                                    setOpen(false);
                                }}
                            >
                                <span>{opt}</span>
                                {selected ? (
                                    <CheckIcon className="h-4 w-4 shrink-0 text-gray-600" aria-hidden />
                                ) : (
                                    <span className="h-4 w-4 shrink-0" aria-hidden />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default ProductCatalogPageSizeSelect;
